import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";

import { SERVER_URL } from "../../constants";

const SponsorPage = () => {
  const [sponsors, setSponsors] = useState([]);
  const [sponsorName, setSponsorName] = useState("");
  const [sponsorLogo, setSponsorLogo] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editingSponsorId, setEditingSponsorId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch sponsors
  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/api/sponsors`);
      const {data}=response.data
      setSponsors(data);
    } catch (err) {
      setError("Failed to fetch sponsors");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "unsigned_preset");
    formData.append("cloud_name", "dpgf1rkjl");

    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/dpgf1rkjl/image/upload`,
        formData
      );
      return response.data.secure_url;
    } catch (error) {
      throw new Error("Failed to upload image");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSponsorLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setSponsorLogo(null);
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError(null);

    try {
      let logoUrl = sponsorLogo ? await uploadToCloudinary(sponsorLogo) : null;

      if (editingSponsorId) {
        const response = await axios.put(
          `${SERVER_URL}/api/sponsors/${editingSponsorId}`,
          {
            name: sponsorName,
            ...(logoUrl && { logo: logoUrl })
          }
        );
        setSponsors((prev) =>
          prev.map((s) => (s._id === editingSponsorId ? response.data : s))
        );
      } else {
        const response = await axios.post(`${SERVER_URL}/api/sponsors`, {
          name: sponsorName,
          logo: logoUrl
        });
        setSponsors((prev) => [...prev, response.data]);
      }

      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save sponsor");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sponsor?")) return;

    try {
      await axios.delete(`${SERVER_URL}/api/sponsors/${id}`);
      setSponsors((prev) => prev.filter((s) => s._id !== id));
    } catch (err) {
      setError("Failed to delete sponsor");
    }
  };

  const resetForm = () => {
    setSponsorName("");
    setSponsorLogo(null);
    setImagePreview(null);
    setEditingSponsorId(null);
    setError(null);
  };

  const handleEdit = (sponsor) => {
    setSponsorName(sponsor.name);
    setEditingSponsorId(sponsor._id);
    setImagePreview(sponsor.logo);
  };

  if (loading) return <div className="text-center p-10">Loading...</div>;

  return (
    <div className="p-10 py-14 bg-gradient-to-br from-green-300 to-blue-300 rounded-lg shadow-lg max-w-3xl mx-auto mt-6 mb-6">
      <motion.h2
        className="text-2xl font-bold mb-6 text-indigo-800 tracking-wide"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        Manage Sponsors 🎉
      </motion.h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          {error}
        </div>
      )}

      <motion.form
        onSubmit={handleSubmit}
        className="bg-white bg-opacity-90 p-6 rounded-lg shadow-md mb-6"
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-semibold text-gray-700">
            Sponsor Name
          </label>
          <input
            type="text"
            id="name"
            value={sponsorName}
            onChange={(e) => setSponsorName(e.target.value)}
            required
            className="w-full p-2 border border-gray-300 rounded-md mt-2"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="logo" className="block text-sm font-semibold text-gray-700">
            Sponsor Logo
          </label>
          <input
            type="file"
            accept="image/*"
            id="logo"
            onChange={handleImageChange}
            className="w-full p-2 border border-gray-300 rounded-md mt-2"
          />
          
          {/* Image Preview Section */}
          {imagePreview && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">Preview:</p>
              <div className="relative w-40 h-40 border rounded-lg overflow-hidden">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setSponsorLogo(null);
                    setImagePreview(null);
                  }}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </div>

        <motion.button
          type="submit"
          className={`mt-4 w-full font-semibold px-4 py-2 rounded-lg ${
            uploading ? "bg-gray-400" : "bg-indigo-600 text-white"
          }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={uploading}
        >
          {uploading
            ? "Processing..."
            : editingSponsorId
            ? "Update Sponsor 🚀"
            : "Add Sponsor 🚀"}
        </motion.button>

        {editingSponsorId && (
          <motion.button
            type="button"
            onClick={resetForm}
            className="mt-2 w-full font-semibold px-4 py-2 rounded-lg bg-gray-500 text-white"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Cancel Edit
          </motion.button>
        )}
      </motion.form>

      <div className="bg-white bg-opacity-90 p-6 rounded-lg shadow-md">
        <h3 className="text-xl font-bold mb-4">Sponsors List 📜</h3>
        {sponsors.length > 0 ? (
          sponsors.map((sponsor) => (
            <motion.div
              key={sponsor._id}
              className="flex items-center justify-between mb-4 p-4 border rounded-lg hover:bg-gray-50"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div>
                <h4 className="font-semibold">{sponsor.name}</h4>
                {sponsor.logo && (
                  <img
                    src={sponsor.logo}
                    alt={sponsor.name}
                    className="w-20 h-20 object-cover rounded-md mt-2"
                  />
                )}
              </div>
              <div className="space-x-2">
                <button
                  className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                  onClick={() => handleEdit(sponsor)}
                >
                  Edit
                </button>
                <button
                  className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                  onClick={() => handleDelete(sponsor._id)}
                >
                  Delete
                </button>
              </div>
            </motion.div>
          ))
        ) : (
          <p className="text-gray-600">No sponsors added yet.</p>
        )}
      </div>
    </div>
  );
};

export default SponsorPage;